import ky from 'ky';

interface SetActingAsResponse {
	STATUS: number;
	MESSAGE?: string;
	DETAIL?: string;
}

interface SetActingAsResult {
	ok: boolean;
	message?: string;
}

// The single acts-as entry point for both flag states (global/components/agents.cfc
// setActingAs, beside getActingAs) -- flag on does the token swap, flag off writes the
// ActingAsPublicID cookie exactly as the client used to. A blank publicID means
// return to self. `ok: false` means the switch did not happen -- refused by the
// server (failed entitlement check or unresolvable publicID) or never answered
// (ky throws on any non-2xx and on network failure; that is caught here so no
// caller has to). Callers must leave the page and both token cookies untouched
// when that happens, not reload. This function never throws.
const setActingAs = async (publicID: string): Promise<SetActingAsResult> => {
	const formData = new FormData();
	formData.append('method', 'setActingAs');
	formData.append('returnformat', 'json');
	formData.append('publicid', publicID);

	try {
		const res = await ky
			.post('/global/components/agents.cfc', { body: formData })
			.json<SetActingAsResponse>();

		if (res.STATUS === 0) {
			return { ok: true };
		}

		// Observed, not verified: the refusals seen from agents.cfc all answer with
		// MESSAGE "Not Authorized" and put the actual reason -- a failed entitlement
		// check, an inactive agent, a sync or mint failure -- in DETAIL. agents.cfc
		// belongs to the consuming app, not to bay-ui, so that is a reading of its
		// behaviour rather than a contract this repo can point at. That pair is
		// curated for a user to read, so it is the only shape passed through.
		//
		// Anything else is an unhandled ColdFusion exception, whose Detail carries
		// internals (query text, file paths, stack context). Every caller renders
		// `message` on screen now, so returning it here would put those in front of
		// whoever is on the page. Callers fall back to their own i18n copy when
		// `message` is absent.
		// ponytail: keyed on the MESSAGE string agents.cfc uses today; a typed
		// refusal code on the response would be the real fix if that string moves.
		if (res.MESSAGE === 'Not Authorized') {
			return { ok: false, message: res.DETAIL || res.MESSAGE };
		}

		return { ok: false };
	} catch {
		// ky's own error text names the URL and status; the caller's copy is better.
		return { ok: false };
	}
};

export default setActingAs;
